"use client";
import { useTranslations } from "next-intl";
import React from "react";
import { useStoreState } from "src/hooks/storeHooks";
import { BundleDiscountSystem } from "src/types/bundle-discounts/BundleDiscountSystem";

interface Props {
  offers: BundleDiscountSystem[];
}

const DiscountProgressBar: React.FC<Props> = ({ offers }) => {
  const t = useTranslations("CartSidebar");
  const cart = useStoreState((state) => state.cart);
  if (!cart || !cart.lineItems) return null;

  const cartItemCount = cart.lineItems.reduce((total, item) => total + item.quantity, 0);

  const sortedOffers = [...offers].sort((a, b) => a.minimumItems - b.minimumItems);
  const numberOfOffers = sortedOffers.length;

  const nextOffer = sortedOffers.find((offer) => cartItemCount < offer.minimumItems);
  const remainingToNext = nextOffer ? nextOffer.minimumItems - cartItemCount : 0;

  // Calculate the progress as a percentage of the way to the next offer
  let progressPercent = 0;
  if (sortedOffers.length > 0) {
    if (cartItemCount <= sortedOffers[0].minimumItems) {
      // Before first offer
      progressPercent = (cartItemCount / sortedOffers[0].minimumItems) * (100 / numberOfOffers);
    } else {
      for (let i = 0; i < sortedOffers.length; i++) {
        const current = sortedOffers[i];
        const next = sortedOffers[i + 1];
        const currentStartPercent = ((i + 1) / numberOfOffers) * 100;
        if (!next) {
          // After last offer
          progressPercent = 100;
          break;
        }
        if (cartItemCount <= next.minimumItems) {
          // Between current and next offer
          const segment = next.minimumItems - current.minimumItems;
          const progressInSegment = cartItemCount - current.minimumItems;
          const segmentPercent = (1 / numberOfOffers) * 100;
          progressPercent = currentStartPercent + (progressInSegment / segment) * segmentPercent;
          break;
        }
      }
    }
  }

  return (
    <div className="h-[120px] rounded-md bg-orange-50 p-4">
      {/* Top text */}
      <div className="mb-4 text-center text-sm font-medium">
        {nextOffer ? <>{t("progress_bar_text", { remainingToNext: remainingToNext })}</> : <>{t("best_offer")}</>}
      </div>

      {/* Progress container */}
      <div className="relative h-6">
        {/* Base gray line */}
        <div className="absolute left-0 top-1/2 z-0 h-1 w-full -translate-y-1/2 transform rounded bg-gray-300" />

        {/* Dynamic red progress fill */}
        <div
          className="absolute left-0 top-1/2 z-10 h-1 -translate-y-1/2 transform rounded bg-red-500 transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />

        {/* Circles */}
        {sortedOffers.map((offer, index) => {
          const isReached = cartItemCount >= offer.minimumItems;
          const leftPercent = ((index + 1) / numberOfOffers) * 100;

          return (
            <div
              key={index}
              className="absolute z-20 flex flex-col items-center"
              style={{ left: `calc(${leftPercent}% - 30px)` }}
            >
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 text-sm font-bold ${
                  isReached ? "border-red-500 bg-red-500 text-white" : "border-black bg-white text-black"
                }`}
              >
                ★
              </div>
              <div
                className="mt-1 text-center text-[10px]"
                style={{ width: 40, wordBreak: "break-word", whiteSpace: "normal" }}
              >
                {offer.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiscountProgressBar;
